import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

import { api, queries, unwrap } from "@/api";
import { ErrorNote } from "@/components/ErrorNote";
import { NoticeForm } from "@/components/NoticeForm";
import { Tile } from "@/components/Tile";
import { formatDateTime, formatRelative, pilotName, visibilityWord } from "@/lib/format";

type Tab = "users" | "airframes" | "leases" | "refusals" | "errors" | "notice";

export const Route = createFileRoute("/admin")({
  validateSearch: (search: Record<string, unknown>): { tab: Tab; q: string } => ({
    tab:
      search.tab === "airframes" || search.tab === "leases" || search.tab === "refusals" || search.tab === "errors" || search.tab === "notice"
        ? search.tab
        : "users",
    q: typeof search.q === "string" ? search.q : "",
  }),
  component: Admin,
});

function bytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function Admin() {
  const me = useQuery(queries.me());
  if (me.isPending) return null;
  if (!me.data) return <p className="muted">Sign in to continue.</p>;
  if (!me.data.user.is_admin) return <p className="muted">This page is for administrators.</p>;
  return <Panel />;
}

function Panel() {
  const { tab, q } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [draft, setDraft] = useState(q);
  const overview = useQuery(queries.adminOverview());
  const tabs: { tab: Tab; label: string }[] = [
    { tab: "users", label: "Users" },
    { tab: "airframes", label: "Airframes" },
    { tab: "leases", label: "Booked aircraft" },
    { tab: "refusals", label: "Refused returns" },
    { tab: "errors", label: "Errors" },
    { tab: "notice", label: "Tell the pilots" },
  ];
  const o = overview.data;
  return (
    <div className="space-y-6">
      <h1 className="page-title">Administration</h1>
      <ErrorNote error={overview.error} />
      {o ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="overview">
          <Tile label="Users" value={o.users} note={`${o.admins} admin, ${o.sessions} sessions`} testId="tile-users" />
          <Tile
            label="Airframes"
            value={o.airframes.total}
            note={`${o.airframes.public} public, ${o.airframes.unlisted} unlisted, ${o.airframes.hidden} hidden`}
            testId="tile-airframes"
          />
          <Tile label="Booked now" value={o.leases.active} note={`${o.leases.stuck} stuck`} tone={o.leases.stuck > 0 ? "warn" : undefined} testId="tile-leases" />
          <Tile label="Tech log entries" value={o.versions} note={`${o.flights} flights`} />
          <Tile label="Database" value={bytes(o.database_bytes)} note={`${bytes(o.storage_bytes)} of packages`} />
          <Tile label="Errors, 24 h" value={o.errors_24h} note={`${o.failed_deliveries_24h} failed deliveries, ${o.client_errors_24h} in the pilots' apps`} tone={o.errors_24h > 0 ? "bad" : o.client_errors_24h > 0 ? "warn" : undefined} testId="tile-errors" />
          <Tile label="Refused returns, 24 h" value={o.refusals_24h} note="what the platform would not record" tone={o.refusals_24h > 0 ? "warn" : undefined} testId="tile-refusals" />
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-3">
        <nav className="flex gap-1" aria-label="Section">
          {tabs.map((t) => (
            <Link
              key={t.tab}
              to="/admin"
              search={{ tab: t.tab, q }}
              className={`btn ${t.tab === tab ? "btn-primary" : ""}`}
              data-testid={`admin-tab-${t.tab}`}
            >
              {t.label}
            </Link>
          ))}
        </nav>
        {tab !== "leases" && tab !== "notice" ? (
          <form
            className="ml-auto flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              navigate({ search: { tab, q: draft.trim() } });
            }}
          >
            <input className="input" type="search" aria-label="Search" placeholder="Search" value={draft} onChange={(e) => setDraft(e.target.value)} data-testid="admin-search" />
            <button type="submit" className="btn">
              Search
            </button>
          </form>
        ) : null}
      </div>
      {tab === "users" ? <Users q={q} /> : null}
      {tab === "airframes" ? <Airframes q={q} /> : null}
      {tab === "leases" ? <Leases /> : null}
      {tab === "refusals" ? <Refusals q={q} /> : null}
      {tab === "errors" ? <Errors q={q} /> : null}
      {tab === "notice" ? <NoticeForm /> : null}
    </div>
  );
}

function Users({ q }: { q: string }) {
  const users = useQuery(queries.adminUsers(q));
  return (
    <section className="space-y-2">
      <ErrorNote error={users.error} />
      {users.data ? (
        <div className="overflow-x-auto">
          <table className="table" data-testid="admin-users">
            <thead>
              <tr>
                <th>Handle</th>
                <th>Name</th>
                <th>E-mail</th>
                <th>Owns</th>
                <th>Member of</th>
                <th>Sessions</th>
                <th>Last seen</th>
                <th>Joined</th>
              </tr>
            </thead>
            <tbody>
              {users.data.items.map((u) => (
                <tr key={u.id} data-handle={u.handle}>
                  <td>
                    {u.handle}
                    {u.is_admin ? <span className="pill pill-ok ml-2">admin</span> : null}
                    {u.deleted_at ? <span className="pill pill-bad ml-2">removed</span> : null}
                  </td>
                  <td>{pilotName(u)}</td>
                  <td>{u.email ?? <span className="muted">none</span>}</td>
                  <td className="tnum">{u.airframes_owned}</td>
                  <td className="tnum">{u.memberships}</td>
                  <td className="tnum">{u.sessions}</td>
                  <td>{u.last_seen_at ? formatRelative(u.last_seen_at) : "never"}</td>
                  <td>{formatDateTime(u.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="muted text-xs">{users.data.total} accounts</p>
        </div>
      ) : null}
    </section>
  );
}

function Airframes({ q }: { q: string }) {
  const list = useQuery(queries.adminAirframes(q));
  return (
    <section className="space-y-2">
      <ErrorNote error={list.error} />
      {list.data ? (
        <div className="overflow-x-auto">
          <table className="table" data-testid="admin-airframes">
            <thead>
              <tr>
                <th>Reg</th>
                <th>Name</th>
                <th>Visibility</th>
                <th>Owner</th>
                <th>Members</th>
                <th>Entries</th>
                <th>Flights</th>
                <th>Who has it</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.map((a) => (
                <tr key={a.id} data-reg={a.reg}>
                  <td>
                    <Link to="/airframes/$id" params={{ id: a.id }} search={{ admin: true }} className="underline" data-testid={`admin-open-${a.reg}`}>
                      {a.reg}
                    </Link>
                  </td>
                  <td>{a.name || <span className="muted">none</span>}</td>
                  <td>
                    <span className="pill">{visibilityWord(a.visibility)}</span>
                  </td>
                  <td>{a.owner_handle}</td>
                  <td className="tnum">{a.members}</td>
                  <td className="tnum">{a.versions}</td>
                  <td className="tnum">{a.flights}</td>
                  <td>
                    {a.lease_id ? (
                      <span className={a.lease_stuck ? "text-warn" : ""}>
                        {a.lease_holder}
                        {a.lease_stuck ? " (stuck)" : ""}
                      </span>
                    ) : (
                      <span className="muted">free</span>
                    )}
                  </td>
                  <td>{formatRelative(a.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="muted text-xs">{list.data.total} airframes</p>
        </div>
      ) : null}
    </section>
  );
}

function Leases() {
  const queryClient = useQueryClient();
  const [scope, setScope] = useState<"active" | "all">("active");
  const leases = useQuery(queries.adminLeases(scope));
  const revoke = useMutation({
    mutationFn: async (lid: string) =>
      unwrap(await api.POST("/api/v1/admin/leases/{target}", { params: { path: { target: `${lid}:revoke` } } })),
    // Settled, not success: after a 409 (ended meanwhile) the row must go too.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["admin"] });
    },
  });
  return (
    <section className="space-y-2">
      <div className="flex gap-1">
        <button type="button" className={`btn ${scope === "active" ? "btn-primary" : ""}`} onClick={() => setScope("active")}>
          Active
        </button>
        <button type="button" className={`btn ${scope === "all" ? "btn-primary" : ""}`} onClick={() => setScope("all")}>
          All
        </button>
      </div>
      <ErrorNote error={leases.error ?? revoke.error} />
      {leases.data ? (
        <div className="overflow-x-auto">
          <table className="table" data-testid="admin-leases">
            <thead>
              <tr>
                <th>Reg</th>
                <th>Pilot</th>
                <th>State</th>
                <th>Acquired</th>
                <th>Last heartbeat</th>
                <th>Expires</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {leases.data.items.map((l) => (
                <tr key={l.id} data-lease={l.id} data-stuck={l.stuck ? "true" : "false"}>
                  <td>{l.reg}</td>
                  <td>{l.holder}</td>
                  <td>
                    <span className={`pill ${l.stuck ? "pill-warn" : ""}`}>{l.stuck ? "stuck" : l.state}</span>
                  </td>
                  <td>{formatDateTime(l.acquired_at)}</td>
                  <td>{formatRelative(l.last_heartbeat)}</td>
                  <td>{formatRelative(l.expires_at)}</td>
                  <td>
                    {l.state === "active" ? (
                      <button type="button" className="btn btn-danger" onClick={() => revoke.mutate(l.id)} disabled={revoke.isPending} data-testid="admin-revoke">
                        Revoke
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="muted text-xs">{leases.data.total} booked</p>
        </div>
      ) : null}
    </section>
  );
}

/** The check-ins the platform refused for what they said (F409, F411): the anti-cheat's trail. */
function Refusals({ q }: { q: string }) {
  const list = useQuery(queries.adminRefusals(q));
  return (
    <section className="space-y-2">
      <ErrorNote error={list.error} />
      {list.data ? (
        <div className="overflow-x-auto">
          <table className="table" data-testid="admin-refusals">
            <thead>
              <tr>
                <th>When</th>
                <th>Airframe</th>
                <th>Pilot</th>
                <th>Rule</th>
                <th>What</th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.map((r) => (
                <tr key={r.id}>
                  <td className="tnum">{formatDateTime(r.created_at)}</td>
                  <td>
                    <Link to="/airframes/$id" params={{ id: r.airframe_id }} search={{ admin: true }} className="underline">
                      {r.reg}
                    </Link>
                  </td>
                  <td>{r.pilot ?? "a deleted account"}</td>
                  <td>
                    <span className="pill pill-warn font-mono text-xs">{r.code}</span>
                  </td>
                  <td className="text-sm" title={r.details == null ? undefined : JSON.stringify(r.details)}>
                    {r.message}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="muted text-xs" data-testid="admin-refusals-total">
            {list.data.total} refused returns
          </p>
        </div>
      ) : null}
    </section>
  );
}

function Errors({ q }: { q: string }) {
  const log = useQuery(queries.adminErrors(q));
  const reported = useQuery(queries.adminClientErrors(q));
  return (
    <section className="space-y-4">
      <ErrorNote error={log.error} />
      <ErrorNote error={reported.error} />
      <div>
        <h2 className="mb-2 font-semibold">In the pilots' apps and pages</h2>
        <p className="muted mb-2 text-xs">What a page showed when something failed, as the app or the browser reported it (decision 140): the pilot, the build, the system, the code and the words. Hover a row for where it happened.</p>
        {reported.data ? (
          <div className="overflow-x-auto">
            <table className="table" data-testid="admin-client-errors">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Pilot</th>
                  <th>Build</th>
                  <th>Code</th>
                  <th>Message</th>
                </tr>
              </thead>
              <tbody>
                {reported.data.items.map((e) => (
                  <tr key={e.id} title={JSON.stringify(e.context)} data-testid="client-error-row">
                    <td className="tnum">{formatDateTime(e.created_at)}</td>
                    <td>{e.pilot ?? <span className="muted">a deleted account</span>}</td>
                    <td className="font-mono text-xs">
                      {e.client} {e.version}
                      {e.os ? ` · ${e.os}` : ""}
                    </td>
                    <td className="font-mono text-xs">{e.code}</td>
                    <td>{e.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="muted text-xs" data-testid="admin-client-errors-total">
              {reported.data.total} reported by the pilots' apps and pages
            </p>
          </div>
        ) : null}
      </div>
      <h2 className="font-semibold">On the platform</h2>
      {log.data ? (
        <>
          <div className="overflow-x-auto">
            <table className="table" data-testid="admin-errors">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Status</th>
                  <th>Request</th>
                  <th>Code</th>
                  <th>Message</th>
                </tr>
              </thead>
              <tbody>
                {log.data.items.map((e) => (
                  <tr key={e.id}>
                    <td className="tnum">{formatDateTime(e.created_at)}</td>
                    <td className="tnum">{e.status}</td>
                    <td className="font-mono text-xs">
                      {e.method} {e.path}
                    </td>
                    <td className="font-mono text-xs">{e.code}</td>
                    <td>{e.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="muted text-xs" data-testid="admin-errors-total">
              {log.data.total} server errors
            </p>
          </div>
          <div>
            <h2 className="mb-2 font-semibold">Failed deliveries</h2>
            {log.data.failed_deliveries.length === 0 ? (
              <p className="muted text-sm">None.</p>
            ) : (
              <ul className="space-y-1 text-sm">
                {log.data.failed_deliveries.map((d) => (
                  <li key={d.id} className="flex gap-3">
                    <span className="muted tnum">{formatDateTime(d.created_at)}</span>
                    <span className="pill">{d.channel}</span>
                    <span className="font-mono text-xs">{d.event_type}</span>
                    <span>{d.target}</span>
                    <span className="muted">{d.error}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      ) : null}
    </section>
  );
}
